import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PublishPaymentScreen from "@/components/PublishPaymentScreen";
import PhotoEditor from "@/components/PhotoEditor";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import LocationPicker from "@/components/LocationPicker";

const DEST_TYPES = [
  { key: "person", label: "A person" },
  { key: "organisation", label: "A group / organisation" },
  { key: "cause", label: "A cause / event" },
  { key: "everyone", label: "Everyone" },
];

const VIS = [
  { key: "public", label: "Public" },
  { key: "partial", label: "Partial" },
  { key: "anonymous", label: "Anonymous" },
];

const EMPTY_FORM = {
  title: "", text: "",
  destination_type: "everyone", destination_label: "",
  origin_country: "", origin_city: "",
  creator_visibility: "public",
  kind: "permanent",
};

export default function CreatePage() {
  const { user, loading, requireAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [moderationState, setModerationState] = useState(null);
  const [publishPayment, setPublishPayment] = useState(null);
  const [approvedAppeal, setApprovedAppeal] = useState(null);

  useEffect(() => {
    // If a previous BLOCKED MESSAGE was appealed and approved, surface a
    // 'Publish now' banner. The resubmission_token skips moderation but
    // still routes through dedup + payment.
    api.get("/appeals/mine")
      .then((r) => {
        const approved = (r.data || []).find(
          (a) => a.status === "approved" && a.resubmission_token);
        if (approved) setApprovedAppeal(approved);
      }).catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPosition, setPhotoPosition] = useState(null);
  const [photoEditing, setPhotoEditing] = useState(false);
  const [underReview, setUnderReview] = useState(null);
  const [stepError, setStepError] = useState("");
  const askedAuthRef = useRef(false);

  useEffect(() => {
    if (!loading && !user && !askedAuthRef.current) {
      askedAuthRef.current = true;
      requireAuth(() => {});
    }
    if (user) askedAuthRef.current = false;
    // eslint-disable-next-line
  }, [loading, user]);

  if (!user) {
    return <div className="max-w-2xl mx-auto px-6 py-24 oh-label">Sign in to start a message.</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = (s) => {
    if (s === 0) return form.title.trim().length > 0 && form.text.trim().length > 0;
    if (s === 2) return !!country && form.origin_country.trim().length > 0;
    return true;
  };
  const stepErrorMessage = (s) => {
    if (s === 0) {
      if (!form.title.trim()) return "A title is required.";
      if (!form.text.trim()) return "A message is required.";
    }
    if (s === 2) {
      if (!form.origin_country.trim()) return "Country is required — every MESSAGE has an origin.";
      if (!country) return "Please pick a country from the suggestions.";
    }
    return "";
  };

  const goNext = () => {
    if (!stepValid(step)) { setStepError(stepErrorMessage(step)); return; }
    setStepError(""); setStep(step + 1);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(r.data.url);
      setPhotoPosition(null); // reset framing whenever a new photo is chosen
      toast.success("Photo added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const buildPayload = () => ({
    title: form.title, text: form.text,
    destination_type: form.destination_type,
    destination_label: form.destination_type === "everyone" ? null : form.destination_label,
    creator_visibility: form.creator_visibility,
    kind: form.kind,
    photo_url: photoUrl,
    photo_position: photoPosition,
    origin_country: country?.country || form.origin_country,
    origin_country_code: country?.country_code || null,
    origin_city: city?.city || null,
    origin_region: city?.region || country?.region || null,
    origin_lat: city?.lat || country?.lat || null,
    origin_lon: city?.lon || country?.lon || null,
    origin_display_name: city?.display_name || country?.display_name || null,
    // If the creator earlier had this MESSAGE blocked and appealed it
    // successfully, the token skips moderation on this resubmit.
    resubmission_token: approvedAppeal?.resubmission_token,
  });

  const publish = async () => {
    if (!form.title.trim() || !form.text.trim()) {
      toast.error("A title and a message are required.");
      setStep(0); setStepError("A title and a message are required.");
      return;
    }
    if (!form.origin_country.trim()) {
      toast.error("Country is required — every MESSAGE has an origin.");
      setStep(2); setStepError("Country is required — every MESSAGE has an origin.");
      return;
    }
    if (!country) {
      toast.error("Please pick a country from the suggestions.");
      setStep(2); setStepError("Please pick a country from the suggestions.");
      return;
    }
    setBusy(true); setStepError("");
    const tid = toast.loading("Checking your MESSAGE…");
    try {
      const r = await api.post("/messages", buildPayload());
      toast.dismiss(tid);
      if (r.data.duplicate) { setDuplicate(r.data); return; }
      // SAFETY GATE — the backend refused to publish and refused to charge.
      if (r.data.moderation?.state === "BLOCKED") {
        setModerationState({ state: "BLOCKED" });
        return;
      }
      if (r.data.moderation?.state === "REVIEW") {
        setModerationState({ state: "REVIEW", reviewId: r.data.moderation.review_id });
        return;
      }
      // €1 CREATE FLOW — the MESSAGE is pending_payment on the server. Open
      // a Stripe checkout for the creator's own first VOICE. On success the
      // MESSAGE goes live and we navigate to it. If the creator abandons
      // checkout, the pending doc is hidden from public feeds and can be
      // safely retried later (idempotent client_secret).
      if (r.data.payment?.requires_payment && r.data.payment?.client_secret) {
        setPublishPayment({
          messageId: r.data.message.id,
          clientSecret: r.data.payment.client_secret,
          providerIntentId: r.data.payment.provider_intent_id,
          title: r.data.message.title,
        });
        return;
      }
      if (r.data.review_required) {
        toast.info("Our system is busy — sending your MESSAGE for a quick human review.",
                   { duration: 5000 });
        try {
          const rr = await api.post("/messages/request-review", {
            ...buildPayload(),
            matched_message_id: null,
            review_reason: "auto: semantic dedup unavailable at publish time",
          });
          setUnderReview({
            reviewId: rr?.data?.review_id,
            title: form.title,
            text: form.text,
            createdAt: new Date().toISOString(),
          });
        } catch (_) { /* best effort */ }
        return;
      }
      toast.success("Your MESSAGE is alive. You are Voice #1.");
      navigate(`/message/${r.data.message.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not publish. Please try again.", { id: tid });
    } finally { setBusy(false); }
  };

  const requestReview = async () => {
    setBusy(true);
    const tid = toast.loading("Sending your draft to review…");
    try {
      const rr = await api.post("/messages/request-review", {
        ...buildPayload(),
        matched_message_id: duplicate?.message?.id || null,
        review_reason: "creator believes this MESSAGE is semantically different",
      });
      toast.success("Draft received — a human will review it.", { id: tid, duration: 5000 });
      setUnderReview({
        reviewId: rr?.data?.review_id,
        title: form.title,
        text: form.text,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not send for review.", { id: tid });
    } finally { setBusy(false); }
  };

  if (underReview) {
    return (
      <div className="max-w-[720px] mx-auto px-6 sm:px-10 py-16" data-testid="under-review-screen">
        <div className="oh-label mb-3">STATUS · UNDER REVIEW</div>
        <h1 className="text-3xl sm:text-4xl font-serif mb-4">Your draft is safe.</h1>
        <p className="text-sm sm:text-base opacity-80 mb-8">
          A human reviewer will look at this MESSAGE shortly. You will not be
          charged while it is under review. Nothing has been lost — every field
          you entered is stored exactly as you wrote it.
        </p>
        <div className="border oh-hairline p-5 mb-8">
          <div className="oh-label mb-2">DRAFT</div>
          <div className="font-serif text-xl mb-2">{underReview.title || "(untitled)"}</div>
          <div className="text-sm opacity-80 whitespace-pre-wrap">
            {(underReview.text || "").slice(0, 240)}
            {(underReview.text || "").length > 240 ? "…" : ""}
          </div>
          <div className="oh-label mt-4">
            SUBMITTED · {new Date(underReview.createdAt).toLocaleString()}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="oh-btn-primary"
            data-testid="under-review-view-mine"
            onClick={() => navigate("/account/under-review")}>
            VIEW MY DRAFTS UNDER REVIEW
          </button>
          <button
            type="button"
            className="oh-btn-ghost"
            data-testid="under-review-start-another"
            onClick={() => {
              setUnderReview(null);
              setForm(EMPTY_FORM); setCountry(null); setCity(null);
              setPhotoUrl(null); setPhotoPosition(null);
              setDuplicate(null); setStep(0);
            }}>
            START ANOTHER MESSAGE
          </button>
        </div>
      </div>
    );
  }

  if (publishPayment) {
    return <PublishPaymentScreen payment={publishPayment}
      onSuccess={(msg) => {
        toast.success("Your MESSAGE is alive. You are Voice #1.");
        navigate(`/message/${msg?.id || publishPayment.messageId}`);
      }}
      onCancel={() => setPublishPayment(null)} />;
  }

  if (moderationState?.state === "BLOCKED") {
    return (
      <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="mod-blocked-screen">
        <div className="oh-label">Safety review</div>
        <h1 className="font-serif-editorial text-5xl sm:text-6xl mt-4 leading-[1.02]" data-testid="mod-blocked-headline">
          This MESSAGE can’t be published.
        </h1>
        <p className="text-[color:var(--oh-ink-2)] mt-6 text-lg leading-relaxed">
          It may violate VOIHU’s community standards. <br />
          You have not been charged.
        </p>
        <p className="oh-label mt-6 text-[color:var(--oh-ink-3)]">
          VOIHU exists for human expression. If you believe this decision is wrong,
          you can appeal below and a human will review your MESSAGE.
        </p>
        <div className="mt-10 flex flex-col gap-3">
          <button onClick={async () => {
            try {
              const p = buildPayload();
              const r = await api.post("/appeals", {
                rejected_title: p.title, rejected_text: p.text,
                destination_type: p.destination_type,
                destination_label: p.destination_label,
                origin_country: p.origin_country,
                photo_url: p.photo_url,
                appeal_text: "Please review this MESSAGE. I believe it meets VOIHU's community standards.",
              });
              if (r.data.already_open) {
                toast.info("Your appeal is already open — a human will look shortly.");
              } else {
                toast.success("Your appeal is with a human. You will hear back by email.");
              }
              setModerationState((s) => ({ ...s, appealed: true,
                                          appealId: r.data.appeal_id || s.appealId }));
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not submit appeal.");
            }
          }}
          disabled={moderationState?.appealed}
          className="btn-red w-full disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="mod-blocked-appeal">
            {moderationState?.appealed ? "Appeal pending review" : "Appeal to a human"}
          </button>
          <button onClick={() => { setModerationState(null); setStep(0); }}
                  className="btn-ghost w-full" data-testid="mod-blocked-edit">
            Edit and try a different MESSAGE
          </button>
          <button onClick={() => navigate("/")}
                  className="btn-ghost w-full" data-testid="mod-blocked-home">
            Back to VOIHU
          </button>
        </div>
      </div>
    );
  }

  if (moderationState?.state === "REVIEW") {
    return (
      <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="mod-review-screen">
        <div className="oh-label">Quick review</div>
        <h1 className="font-serif-editorial text-5xl sm:text-6xl mt-4 leading-[1.02]" data-testid="mod-review-headline">
          This MESSAGE needs a quick review.
        </h1>
        <p className="text-[color:var(--oh-ink-2)] mt-6 text-lg leading-relaxed">
          A human will take a look before it goes live. This is a routine check — nothing has gone wrong. <br />
          You have not been charged. Your draft is safe.
        </p>
        <div className="mt-10 border oh-hairline p-8">
          <div className="oh-label">Your draft is saved</div>
          <div className="font-serif-editorial text-2xl mt-3" data-testid="mod-review-draft">
            {form.title}
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <button onClick={() => {
                    setUnderReview({
                      reviewId: moderationState?.reviewId,
                      title: form.title,
                      text: form.text,
                      createdAt: new Date().toISOString(),
                    });
                    setModerationState(null);
                  }}
                  className="btn-red w-full" data-testid="mod-review-request">
            Send for review
          </button>
          <button onClick={() => { setModerationState(null); setStep(0); }}
                  className="btn-ghost w-full" data-testid="mod-review-edit">
            Edit my MESSAGE
          </button>
        </div>
      </div>
    );
  }

  if (duplicate) {
    return (
      <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="dup-screen">
        <div className="oh-label">One meaning · One message</div>
        <h1 className="font-serif-editorial text-5xl sm:text-6xl mt-4 leading-[1.02]" data-testid="dup-headline">
          This meaning is already moving.
        </h1>
        <p className="text-[color:var(--oh-ink-2)] mt-6 text-lg leading-relaxed">
          People are already making this message heard. Add your voice to the existing MESSAGE and make it louder.
        </p>
        <div className="mt-10 border oh-hairline p-8">
          <div className="oh-label">
            {duplicate.message.destination_label || "For everyone"}
            {duplicate.message.origin_country ? ` · ${duplicate.message.origin_country}` : ""}
          </div>
          <div className="font-serif-editorial text-3xl mt-3" data-testid="dup-title">
            {duplicate.message.title}
          </div>
          <div className="oh-label mt-4" data-testid="dup-voices">
            {duplicate.message.voices_count.toLocaleString()} voices
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <button onClick={() => navigate(`/message/${duplicate.message.id}`)}
                  className="btn-red w-full" data-testid="dup-goto">
            Go to this message
          </button>
          <button onClick={requestReview} disabled={busy}
                  className="btn-ghost w-full" data-testid="dup-review">
            {busy ? "Sending…" : "I believe this is different — request review"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-10 pt-16 pb-24">
      {approvedAppeal && (
        <div className="mb-8 border oh-hairline p-5 bg-[color:var(--oh-bg-2)]"
             data-testid="approved-appeal-banner">
          <div className="oh-label text-[color:var(--oh-red)]">Appeal approved</div>
          <div className="font-serif-editorial text-xl mt-2">
            A human reviewed your appeal. You can publish this MESSAGE now — moderation is skipped, payment still applies.
          </div>
        </div>
      )}
      <div className="oh-label">Start a message · Step {step + 1} / 3</div>
      <div className="mt-2 mb-10 h-px bg-[color:var(--oh-border)] relative">
        <div className="absolute top-0 left-0 h-px bg-[color:var(--oh-ink)] transition-all"
             style={{ width: `${(step + 1) * 33.34}%` }} />
      </div>

      {step === 0 && (
        <div>
          <h1 className="font-serif-editorial text-4xl sm:text-5xl leading-tight">
            What do you want people to hear?
          </h1>
          <div className="mt-10">
            <label className="oh-label">Title · {form.title.length}/50</label>
            <input value={form.title} maxLength={50}
                   onChange={(e) => set("title", e.target.value.toUpperCase())}
                   placeholder="Give your message a name"
                   className="oh-input font-serif-editorial text-2xl uppercase mt-2"
                   data-testid="create-title" />
          </div>
          <div className="mt-8">
            <label className="oh-label">Message · {form.text.length}/200</label>
            <textarea value={form.text} maxLength={200} rows={3}
                      onChange={(e) => set("text", e.target.value)}
                      placeholder="Say it in 200 characters or less"
                      className="oh-input font-serif-editorial text-xl mt-2 resize-none"
                      data-testid="create-text" />
          </div>
          <div className="mt-8">
            <label className="oh-label">Photo · optional</label>
            <div className="mt-3">
              {photoUrl ? (
                <>
                  <div className="aspect-[16/9] overflow-hidden bg-[color:var(--oh-bg-2)] border oh-hairline relative"
                       data-testid="create-photo-preview">
                    <img
                      src={photoUrl.startsWith("http") ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                      alt=""
                      className="absolute top-1/2 left-1/2 pointer-events-none"
                      style={photoPosition ? {
                        transform: `translate(-50%, -50%) translate(${photoPosition.x}px, ${photoPosition.y}px) scale(${photoPosition.scale})`,
                        width: "100%", height: "100%", objectFit: "cover",
                      } : {
                        transform: "translate(-50%, -50%)",
                        width: "100%", height: "100%", objectFit: "cover",
                      }} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <label className="oh-btn-ghost cursor-pointer" data-testid="create-photo-change">
                      CHANGE PHOTO
                      <input type="file" accept="image/*" onChange={upload} className="hidden" />
                    </label>
                    <button type="button" className="oh-btn-ghost"
                            data-testid="create-photo-edit"
                            onClick={() => setPhotoEditing(true)}>
                      EDIT PHOTO
                    </button>
                    <button type="button" className="oh-btn-ghost"
                            data-testid="create-photo-remove"
                            onClick={() => { setPhotoUrl(null); setPhotoPosition(null); }}>
                      REMOVE PHOTO
                    </button>
                  </div>
                </>
              ) : (
                <label className="block border oh-hairline aspect-[16/9] flex items-center justify-center cursor-pointer hover:bg-[color:var(--oh-bg-2)]"
                       data-testid="create-photo-label">
                  <span className="oh-label">Add a photo</span>
                  <input type="file" accept="image/*" onChange={upload} className="hidden"
                         data-testid="create-photo-input" />
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {photoEditing && photoUrl && (
        <PhotoEditor
          src={photoUrl.startsWith("http") ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
          initial={photoPosition || { x: 0, y: 0, scale: 1 }}
          onCancel={() => setPhotoEditing(false)}
          onSave={(frame) => { setPhotoPosition(frame); setPhotoEditing(false); }}
        />
      )}

      {step === 1 && (
        <div>
          <h1 className="font-serif-editorial text-4xl sm:text-5xl leading-tight">
            Who or what should hear this?
          </h1>
          <div className="mt-8 grid grid-cols-2 gap-2">
            {DEST_TYPES.map((d) => (
              <button key={d.key} onClick={() => set("destination_type", d.key)}
                      className={`p-6 text-left border oh-hairline transition-colors ${form.destination_type === d.key ? "bg-[color:var(--oh-ink)] text-[color:var(--oh-bg)] border-[color:var(--oh-ink)]" : "hover:bg-[color:var(--oh-bg-2)]"}`}
                      data-testid={`create-dest-${d.key}`}>
                {d.label}
              </button>
            ))}
          </div>
          {form.destination_type !== "everyone" && (
            <div className="mt-6">
              <label className="oh-label">Destination name · optional</label>
              <input value={form.destination_label} onChange={(e) => set("destination_label", e.target.value)}
                     placeholder="Who or what this is for"
                     className="oh-input" data-testid="create-dest-label" />
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <h1 className="font-serif-editorial text-4xl sm:text-5xl leading-tight">
            Where does it start? How should you appear?
          </h1>
          <div className="mt-10">
            <label className="oh-label">Country · required</label>
            <LocationPicker
              mode="country" value={form.origin_country} selected={country}
              onChange={(v) => { set("origin_country", v); if (!v) setCountry(null); }}
              onSelect={(sel) => {
                setCountry(sel);
                if (sel) { set("origin_country", sel.country); setStepError(""); }
                if (sel && city && (city.country_code || "").toUpperCase() !== sel.country_code) {
                  setCity(null); set("origin_city", "");
                }
              }}
              placeholder="Search for a country" testid="create-country" autoFocus />
          </div>
          <div className="mt-8">
            <label className="oh-label">City · optional{country ? "" : " (pick a country first)"}</label>
            <LocationPicker
              mode="city" value={form.origin_city} selected={city}
              countryCode={country?.country_code}
              onChange={(v) => { set("origin_city", v); if (!v) setCity(null); }}
              onSelect={(sel) => { setCity(sel); if (sel) set("origin_city", sel.city || sel.display_name.split(",")[0].trim()); }}
              placeholder={country ? "Search for a city or locality" : "Search for a city (optional)"}
              testid="create-city" />
          </div>
          <div className="mt-10 border-t oh-hairline pt-8">
            <label className="oh-label">How should you appear</label>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {VIS.map((v) => (
                <button key={v.key} onClick={() => set("creator_visibility", v.key)}
                        className={`p-4 border oh-hairline transition-colors ${form.creator_visibility === v.key ? "bg-[color:var(--oh-ink)] text-[color:var(--oh-bg)] border-[color:var(--oh-ink)]" : "hover:bg-[color:var(--oh-bg-2)]"}`}
                        data-testid={`create-vis-${v.key}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-12 flex flex-col gap-3">
        {stepError && (
          <div className="oh-label-error" data-testid="create-step-error" role="alert">
            {stepError}
          </div>
        )}
        <div className="flex gap-3 justify-between">
          <button onClick={() => (step === 0 ? navigate("/") : (setStepError(""), setStep(step - 1)))}
                  className="btn-ghost" data-testid="create-back">
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < 2 ? (
            <button onClick={goNext} className="btn-primary" data-testid="create-next">
              Continue →
            </button>
          ) : (
            <button onClick={publish} disabled={busy} className="btn-red" data-testid="create-publish">
              {busy ? "Checking…" : "Check message · Publish"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
